import React, { useState } from 'react';
import ReactQuill from 'react-quill';
import { grey } from '@mui/material/colors';
import { Info } from '@mui/icons-material';
import makeStyles from '@mui/styles/makeStyles';
import { useFormContext } from 'react-hook-form';
import { Box, Stack, Tooltip, Typography, Button } from '@mui/material';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';
import { useFPDGenerator } from '../../hooks';
import { FinanceProgramFormValues } from '../../types';

interface Props {
  infoText?: string;
}

export const OptionSummaryDetails = ({ infoText = '' }: Props) => {
  const classes = useStyles();

  const { getValues, setValue } = useFormContext<FinanceProgramFormValues>();

  const [isGenActive, setIsGenActive] = useState<boolean>(() => !getValues('financeProgramDescription.editorValue'));

  useFPDGenerator(isGenActive);

  return (
    <Box className={classes.container}>
      <Stack className={classes.panel}>
        <Stack alignItems="center" gap="0.5rem" direction="row">
          <Typography component="span" variant="subtitle2">
            Finance Program Description
          </Typography>

          <RenderWithCondition condition={!!infoText}>
            <Tooltip title={infoText}>
              <Info fontSize="small" color="primary" />
            </Tooltip>
          </RenderWithCondition>
        </Stack>

        <Button
          size="small"
          variant="contained"
          disabled={isGenActive}
          onClick={() => {
            setIsGenActive(true);
          }}
        >
          Reset changes
        </Button>
      </Stack>

      <Box className={classes.root}>
        <ReactQuill
          theme="snow"
          value={getValues('financeProgramDescription.editorValue')}
          modules={{
            toolbar: [
              [{ header: [1, 2, false] }],
              ['bold', 'italic', 'underline'],
              [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
            ],
          }}
          onChange={(content, delta, source, editor) => {
            if (source === 'user') {
              setIsGenActive(false);
              const currentDelta = editor.getContents();
              const html = editor.getHTML();
              setValue('financeProgramDescription.editorValue', currentDelta);
              setValue('financeProgramDescription.apiValue', html);
            }
          }}
        />
      </Box>
    </Box>
  );
};

const useStyles = makeStyles({
  container: {
    gap: '12px',
    display: 'flex',
    flexDirection: 'column',
  },
  panel: {
    gap: '0.5rem',
    padding: '0.5rem',
    borderRadius: '2px',
    alignItems: 'center',
    flexDirection: 'row',
    backgroundColor: grey['A100'],
    justifyContent: 'space-between',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
  },
  root: {
    '& .ql-tooltip.ql-hidden': {
      display: 'none',
    },
  },
});
